import type { BffSubmitResponse } from '@/services/rivaas-api';
import type { AppStateAction } from '@/providers/app-provider/types';
import type { FailReasonValues, Workflow } from '@repo/common';
import type { Dispatch } from 'react';

import { setSubmitErrorContextAction, setWorkflowAction } from '@/providers/app-provider/actions';
import { mapBffErrorCodeToFailReason } from '@/utils/bff-error-mapping';
import { nextWorkflowFromSubmit, stepToWorkflow, type StepWorkflowOptions } from '@/utils/step-workflow';

const TERMINAL_BFF_STEPS = new Set(['complete', 'enrolment_pending', 'fail']);

export type SubmitErrorContext = {
  failReason: FailReasonValues;
  message?: string;
  retryable: boolean;
  step: string;
  stepData?: Record<string, unknown>;
};

export function isRetryableBffStep(step: string): boolean {
  return !TERMINAL_BFF_STEPS.has(step);
}

export function buildSubmitErrorContext(response: BffSubmitResponse): SubmitErrorContext | null {
  if (!response.error) {
    return null;
  }

  return {
    step: response.nextStep.step,
    stepData: response.nextStep.data,
    retryable: response.error.retryable === true,
    failReason: mapBffErrorCodeToFailReason(response.error.code),
    message: response.error.message,
  };
}

export function workflowForSubmitErrorRetry(
  context: SubmitErrorContext | null | undefined,
  opts: Omit<StepWorkflowOptions, 'data'> = {},
): Workflow | null {
  if (!context?.retryable || !isRetryableBffStep(context.step)) {
    return null;
  }

  return stepToWorkflow(context.step, { ...opts, data: context.stepData });
}

type RedirectUrls = Pick<StepWorkflowOptions, 'unverifiedUrl' | 'verifiedUrl'>;

export function applyBffSubmitResponse(
  dispatch: Dispatch<AppStateAction>,
  response: BffSubmitResponse,
  redirect: RedirectUrls,
): boolean {
  if (response.error) {
    dispatch(setSubmitErrorContextAction(buildSubmitErrorContext(response)));

    return true;
  }

  dispatch(setSubmitErrorContextAction(null));

  const workflowBase = nextWorkflowFromSubmit(response, redirect);

  if (workflowBase) {
    dispatch(setWorkflowAction(workflowBase));
  }

  return false;
}

export function retrySubmitErrorStep(
  dispatch: Dispatch<AppStateAction>,
  context: SubmitErrorContext | null | undefined,
  redirect: RedirectUrls,
): void {
  const workflow = workflowForSubmitErrorRetry(context, redirect);

  if (workflow) {
    dispatch(setWorkflowAction(workflow));
  }
}
